import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import IIcn from './inboxIcons';

const TYPE_MAP = {
    team_invite: { label: 'Mentioned you', bucket: 'mention', icon: IIcn.at },
    team_added: { label: 'Team update', bucket: 'system', icon: IIcn.system },
    team_removed: { label: 'Team update', bucket: 'system', icon: IIcn.system },
    project_assigned: { label: 'Awaiting your approval', bucket: 'approval', icon: IIcn.approve },
    autotranslate_complete: { label: 'System update', bucket: 'system', icon: IIcn.system },
    new_message: { label: 'Commented', bucket: 'comment', icon: IIcn.comment },
    mention: { label: 'Mentioned you', bucket: 'mention', icon: IIcn.at },
    usage_warning: { label: 'Usage alert', bucket: 'system', icon: IIcn.system },
    usage_limit: { label: 'Usage limit reached', bucket: 'system', icon: IIcn.system },
    security_2fa: { label: 'Security', bucket: 'system', icon: IIcn.system },
    integration_reauth: { label: 'Integration needs attention', bucket: 'system', icon: IIcn.system },
    integration_warning: { label: 'Integration alert', bucket: 'system', icon: IIcn.system },
    integration_sync: { label: 'Source updated', bucket: 'system', icon: IIcn.system },
    billing_update: { label: 'Billing update', bucket: 'system', icon: IIcn.system },
    default: { label: 'Notification', bucket: 'system', icon: IIcn.system },
};

const BUCKETS = [
    ['mention', 'Mentions', IIcn.at],
    ['comment', 'Comments', IIcn.comment],
    ['approval', 'Approvals', IIcn.approve],
    ['system', 'System', IIcn.system],
];

const initialsOf = (email) => (email || '?').slice(0, 2).toUpperCase();
const avPaletteFor = (s) => `av-${(((s || '').charCodeAt(0) || 1) % 6) + 1}`;

const typeColor = (t) => ({
    mention: 'var(--accent)',
    comment: 'var(--indigo)',
    approval: 'var(--green)',
    system: 'var(--ink-3)',
}[t] || 'var(--ink-3)');
const typeBg = (t) => ({
    mention: 'var(--accent-soft)',
    comment: 'var(--indigo-soft)',
    approval: 'var(--green-soft)',
    system: 'var(--bg-soft)',
}[t] || 'var(--bg-soft)');

const fmtFull = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const fmtRel = (iso) => {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.round(ms / 60000);
    if (m < 1) return 'now';
    if (m < 60) return `${m} min`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
};

const InboxRail = ({ messages, activeFilter, setActiveFilter, showUnread, setShowUnread }) => {
    const unreadCount = messages.filter((m) => m.unread).length;
    const counts = BUCKETS.reduce((acc, [k]) => {
        acc[k] = messages.filter((m) => m.bucket === k).length;
        return acc;
    }, {});
    return (
        <aside className="rs-inbox-rail">
            <div className="ihead">Inbox</div>
            <div
                className={`rs-inbox-item ${!showUnread && activeFilter === 'all' ? 'on' : ''}`}
                onClick={() => { setActiveFilter('all'); setShowUnread(false); }}
                role="button"
                tabIndex={0}
            >
                <span className="ic">{IIcn.inbox}</span>
                <span>All messages</span>
                <span className="ct">{messages.length}</span>
            </div>
            <div
                className={`rs-inbox-item ${showUnread ? 'on' : ''}`}
                onClick={() => { setShowUnread(true); setActiveFilter('all'); }}
                role="button"
                tabIndex={0}
            >
                <span className="ic">{IIcn.flag}</span>
                <span>Unread</span>
                <span className="ct dot">{unreadCount}</span>
            </div>

            <div className="ihead">Filter by type</div>
            {BUCKETS.map(([key, name, ic]) => (
                <div
                    key={key}
                    className={`rs-inbox-item ${activeFilter === key && !showUnread ? 'on' : ''}`}
                    onClick={() => { setActiveFilter(key); setShowUnread(false); }}
                    role="button"
                    tabIndex={0}
                >
                    <span className="ic">{ic}</span>
                    <span>{name}</span>
                    <span className="ct">{counts[key] || 0}</span>
                </div>
            ))}
        </aside>
    );
};

const MessageRow = ({ msg, active, onClick }) => {
    const typeLabels = { mention: '@mention', comment: 'Comment', approval: 'Approval', system: 'System' };
    return (
        <div className={`rs-msg ${msg.unread ? 'unread' : ''} ${active ? 'active' : ''}`} onClick={onClick}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                <span className="ud" />
                <span className={`av-s ${msg.av}`}>{msg.initials}</span>
            </div>
            <div className="msg-r">
                <div className="msg-from">
                    <b>{msg.from}</b>
                    {msg.lang && <span className="lang-tag">{msg.lang}</span>}
                    <span className="when">{msg.when}</span>
                </div>
                <div className="msg-subj">{msg.subject}</div>
                <div className="msg-prev">{msg.preview}</div>
                <div className="msg-foot">
                    <span
                        className="pill type-c"
                        style={{ color: typeColor(msg.bucket), background: typeBg(msg.bucket), borderColor: 'transparent' }}
                    >
                        {typeLabels[msg.bucket]}
                    </span>
                    {msg.ctxName && <><span>·</span><span>{msg.ctxName}</span></>}
                </div>
            </div>
        </div>
    );
};

const InboxList = ({ messages, activeId, setActiveId, filterName, onMarkAllRead }) => (
    <div className="rs-inbox-list">
        <div className="rs-inbox-listh">
            <h3>{filterName}</h3>
            <span className="ct">{messages.length} {messages.length === 1 ? 'message' : 'messages'}</span>
            <span className="spacer" />
            <button type="button" className="btn-q">{IIcn.filter}<span>Sort</span></button>
            <button type="button" className="btn-q" onClick={onMarkAllRead}>{IIcn.done}<span>Mark all read</span></button>
        </div>
        {messages.length === 0 ? (
            <div style={{ padding: 56, textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: 'var(--ink)' }}>All clear</div>
                <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Nothing matches this filter.</div>
            </div>
        ) : (
            messages.map((msg) => (
                <MessageRow
                    key={msg.id}
                    msg={msg}
                    active={msg.id === activeId}
                    onClick={() => setActiveId(msg.id)}
                />
            ))
        )}
    </div>
);

const InboxDetail = ({ msg, onArchive, onMarkRead }) => {
    const navigate = useNavigate();
    if (!msg) {
        return (
            <div className="rs-inbox-detail">
                <div style={{ padding: 56, textAlign: 'center' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: 'var(--ink)' }}>Select a message</div>
                    <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Pick something from the list to read it here.</div>
                </div>
            </div>
        );
    }
    return (
        <div className="rs-inbox-detail">
            <div className="rs-inbox-dh">
                <button type="button" className="ic-btn" aria-label="Archive" onClick={onArchive}>{IIcn.archive}</button>
                <button type="button" className="ic-btn" aria-label="Snooze">{IIcn.snooze}</button>
                <button type="button" className="ic-btn" aria-label="Mark done" onClick={onMarkRead}>{IIcn.done}</button>
                <span className="spacer" />
                <button type="button" className="ic-btn" aria-label="More">{IIcn.more}</button>
                {msg.link && (
                    <button type="button" className="btn-primary" onClick={() => navigate(msg.link)}>
                        {IIcn.reply}<span>Open</span>
                    </button>
                )}
            </div>

            <div className="rs-inbox-dc">
                <h2 className="subj">{msg.subject}</h2>

                <div className="from-row">
                    <span className={`av-s ${msg.av}`}>{msg.initials}</span>
                    <div className="who">
                        <b>{msg.from}</b>
                        <span>{msg.kind}{msg.lang ? ` · ${msg.lang}` : ''}</span>
                    </div>
                    <span className="when">{msg.whenFull}</span>
                </div>

                <p>{msg.body}</p>

                {msg.ctxName && (
                    <div className="rs-inbox-context">
                        <span className="fic">{msg.ctxExt || 'DOC'}</span>
                        <div className="body">
                            <b>{msg.ctxName}</b>
                            {msg.ctxFolder && <span>{msg.ctxFolder}</span>}
                        </div>
                        {msg.link && (
                            <a className="open-link" onClick={() => navigate(msg.link)}>
                                Open {IIcn.arrow}
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const Inbox = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [raw, setRaw] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('all');
    const [showUnread, setShowUnread] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/notifications', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setRaw(Array.isArray(r.data) ? r.data : []);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load inbox.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    // Live updates: when a mention (or other notification) arrives via socket,
    // re-fetch the inbox so the new row shows up without a page reload.
    useEffect(() => {
        if (!token) return undefined;
        const socket = typeof window !== 'undefined' ? window.__SOCKET__ : null;
        if (!socket) return undefined;
        const refetch = () => {
            axios
                .get('/api/notifications', { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => setRaw(Array.isArray(r.data) ? r.data : []))
                .catch(() => { /* soft fail */ });
        };
        socket.on('commentMention', refetch);
        socket.on('notification:created', refetch);
        return () => {
            socket.off('commentMention', refetch);
            socket.off('notification:created', refetch);
        };
    }, [token]);

    const messages = useMemo(() => raw.map((n) => {
        const map = TYPE_MAP[n.type] || TYPE_MAP.default;
        const fromEmail = n.email || '';
        const fromName = fromEmail.split('@')[0] || 'System';
        const subject = (n.message || '').slice(0, 80);
        return {
            id: n._id,
            unread: !n.read,
            bucket: map.bucket,
            kind: map.label,
            from: fromName,
            initials: initialsOf(fromEmail),
            av: avPaletteFor(fromEmail),
            lang: null,
            when: fmtRel(n.createdAt),
            whenFull: fmtFull(n.createdAt),
            subject: subject || map.label,
            preview: n.message || '',
            body: n.message || '',
            ctxName: null,
            ctxFolder: null,
            ctxExt: null,
            link: n.link || null,
        };
    }), [raw]);

    const filtered = useMemo(() => {
        let r = messages;
        if (showUnread) r = r.filter((m) => m.unread);
        if (activeFilter !== 'all') r = r.filter((m) => m.bucket === activeFilter);
        return r;
    }, [messages, activeFilter, showUnread]);

    useEffect(() => {
        if (!activeId && filtered.length > 0) setActiveId(filtered[0].id);
        if (activeId && !filtered.find((m) => m.id === activeId)) setActiveId(filtered[0] ? filtered[0].id : null);
    }, [filtered, activeId]);

    const active = filtered.find((m) => m.id === activeId);

    const markRead = async (id) => {
        try {
            await axios.patch(`/api/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
        } catch (e) {
            // soft fail — UI still updates
        }
        setRaw((arr) => arr.map((n) => (n._id === id ? { ...n, read: true } : n)));
    };

    const markAllRead = async () => {
        try {
            await axios.patch('/api/notifications/read-all', {}, { headers: { Authorization: `Bearer ${token}` } });
        } catch (e) { /* soft */ }
        setRaw((arr) => arr.map((n) => ({ ...n, read: true })));
    };

    const archive = (id) => {
        // Existing backend has no archive concept; hide locally
        setRaw((arr) => arr.filter((n) => n._id !== id));
        setActiveId(null);
    };

    const filterName = showUnread
        ? 'Unread'
        : (activeFilter === 'all' ? 'All messages' : BUCKETS.find(([k]) => k === activeFilter)[1]);

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading inbox…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;

    return (
        <div className="rs-inbox">
            <InboxRail
                messages={messages}
                activeFilter={activeFilter}
                setActiveFilter={setActiveFilter}
                showUnread={showUnread}
                setShowUnread={setShowUnread}
            />
            <InboxList
                messages={filtered}
                activeId={activeId}
                setActiveId={(id) => { setActiveId(id); markRead(id); }}
                filterName={filterName}
                onMarkAllRead={markAllRead}
            />
            <InboxDetail
                msg={active}
                onArchive={() => active && archive(active.id)}
                onMarkRead={() => active && markRead(active.id)}
            />
        </div>
    );
};

export default Inbox;
